import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { HttpRequest } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';

import { TrezorRegistrationDto } from '@app/generated/models/trezor-registration-dto';

import { OtpInputComponent } from '@app/shared/components/otp-input/otp-input.component';

import { TrezorService } from '@app/authentication/shared/services/trezor.service';

import { TwoFactorType } from '@app/authentication/shared/api/two-factor.api';
import { TwoFactorService } from '@app/authentication/shared/services/two-factor.service';
import { LoadState } from '@app/shared/components/two-auth-profile/two-auth-profile.enum';
import { ActionButtonComponent } from '@app/shared/components/form/action-button/action-button.component';
import { ProfileBaseFormComponent } from '@app/shared/components/profile-base-form/profile-base-form.component';

@Component({
  selector: 'app-two-auth-profile',
  templateUrl: './two-auth-profile.component.html',
  styleUrls: ['./two-auth-profile.component.scss'],
  imports: [
    CommonModule,
    ActionButtonComponent,
    TranslateModule,
    AngularSvgIconModule,
    ProfileBaseFormComponent,
    OtpInputComponent,
  ],
})
export class TwoAuthProfileComponent implements OnDestroy {
  @Input() twoFactorType?: TwoFactorType;
  @Input() originalRequest?: HttpRequest<any>;
  @Input() email?: string;
  @Output() otpCompleted = new EventEmitter();
  @Output() otpCanceled = new EventEmitter();
  @ViewChild(OtpInputComponent) otpInputComponent!: OtpInputComponent;

  loadState: LoadState = LoadState.idle;
  trezorTwoFactorLoadState: LoadState = LoadState.idle;
  hasTrezorTwoFactorData = false;

  protected readonly loadStateEnum = LoadState;
  protected readonly twoFactorTypeEnum = TwoFactorType;

  private unsubscribe$ = new Subject<void>();

  constructor(
    public translateService: TranslateService,
    private trezorService: TrezorService,
    private twoFactorService: TwoFactorService,
    private toastrService: ToastrService,
  ) {}

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  //region 2FA Trezor
  performTrezorTwoFactor(): void {
    this.trezorTwoFactorLoadState = LoadState.loading;

    this.trezorService.performTwoFactorAuth$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: this.onTrezorVerifyCompleted.bind(this),
        error: (error) => {
          this.toastrService.error(this.translateService.instant(error.message));
        },
      })
      .add(() => {
        this.trezorTwoFactorLoadState = LoadState.idle;
      });
  }
  //endregion

  //region 2FA Code
  onTwoFactorCodeCompleted(twoFactorCode: string) {
    this.submitTwoFactor(twoFactorCode);
  }
  //endregion

  onTwoFactorCancel() {
    this.twoFactorType = undefined;
    this.originalRequest = undefined;

    this.resetTwoFactorForm();
    this.otpCanceled.emit();
  }

  //region 2FA Trezor
  private onTrezorVerifyCompleted(trezorTwoFactorData: TrezorRegistrationDto) {
    this.hasTrezorTwoFactorData = true;
    this.submitTwoFactor(JSON.stringify(trezorTwoFactorData));
  }
  //endregion

  //region Submit 2FA
  private submitTwoFactor(twoFactorValue: string) {
    if (!twoFactorValue || !this.originalRequest) {
      this.toastrService.error(this.translateService.instant('error.2fa-failure'));
      this.onTwoFactorAuthError();
      return;
    }

    this.loadState = LoadState.loading;

    this.twoFactorService
      .redoRequestWith2FA(this.originalRequest, twoFactorValue, true)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: this.onTwoFactorAuthSuccess.bind(this),
        error: this.onTwoFactorAuthError.bind(this),
      })
      .add(() => {
        this.loadState = LoadState.idle;
      });
  }

  private onTwoFactorAuthSuccess() {
    this.resetTwoFactorForm();
    this.otpCompleted.emit();
  }

  private onTwoFactorAuthError() {
    this.hasTrezorTwoFactorData = false;

    if (this.otpInputComponent) {
      this.otpInputComponent.resetCodeInput();
      this.otpInputComponent.focusOnField(0);
    }
  }
  //endregion

  private resetTwoFactorForm() {
    this.twoFactorType = undefined;
    this.originalRequest = undefined;
    this.hasTrezorTwoFactorData = false;
    this.trezorTwoFactorLoadState = LoadState.idle;

    if (this.otpInputComponent) {
      this.otpInputComponent.resetCodeInput();
      this.otpInputComponent.focusOnField(0);
    }
  }
}
